import { Autocomplete, createFilterOptions, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { getIngredients } from "../data";
import { IngredientOption } from "./IngredientTextInput";
import { capitalizeFirst } from "../textFormattings";

const filter = createFilterOptions<IngredientOption>();

export interface FilterIngredientsProps {
    filterIngredients?: IngredientOption[];
    onFilterIngredients?: (ingredients: IngredientOption[]) => void;
}

export default function FilterIngredients({ filterIngredients, onFilterIngredients }: FilterIngredientsProps) {
    const [ingredients, setIngredients] = useState<IngredientOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const onOpen = async () => {
        if (ingredients.length || loading) return;

        setLoading(true);
        try {
            const res = await getIngredients();
            let ingredients: IngredientOption[] = [];
            for (const aisle of res.aisles) {
                for (const ingredient of aisle.ingredients) {
                    ingredients.push({ name: ingredient });
                }
            }
            for (const ingredient of res.unsorted) {
                ingredients.push({ name: ingredient });
            }
            setIngredients(ingredients);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack>
            <Autocomplete
                value={filterIngredients}
                onChange={(event, newValues) => {
                    const newIngredients = newValues.map(newValue => {
                        if (typeof newValue === "string") {
                            return {
                                name: newValue,
                            };
                        } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            return {
                                name: newValue.inputValue,
                            };
                        } else {
                            return newValue;
                        }
                    });
                    onFilterIngredients?.(newIngredients);
                }}
                filterOptions={(options, params) => filter(options, params)}
                multiple
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                id="filter-ingredient-input"
                size="small"
                onOpen={onOpen}
                options={ingredients}
                loading={loading}
                getOptionLabel={option => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                        return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                        return option.inputValue;
                    }
                    // Regular option
                    return option.name;
                }}
                renderOption={(props, option) => <li {...props}>{capitalizeFirst(option.name)}</li>}
                sx={{ width: 350 }}
                freeSolo
                renderInput={params => <TextField {...params} variant="standard" placeholder="Sök på ingredienser" />}
            />
        </Stack>
    );
}
